import classNames from 'classnames'
import { JSX, ReactNode, useEffect, useState } from 'react'

import styles from './Accordion.module.scss'

export interface IAccordionProps {
  /**
   * Component inner elements
   */
  children?: ReactNode
  /**
   * A Class name for component root node
   */
  className?: string
  /**
   * A Class name for trigger button
   */
  buttonClassName?: string
  /**
   * Spacing size under the accordion
   */
  margin?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  /**
   * Title in accordion trigger button
   */
  title: string
  /**
   * Set opened by default
   */
  opened?: boolean
  /**
   * Disables content padding
   */
  noPadding?: boolean
  /**
   * Callback function fires when accordion is opened
   * @param state - boolean, is the accordion is open
   */
  onOpen?: (state: boolean) => void
  /**
   * Content at the end of the trigger button
   */
  endContent?: () => JSX.Element
  /**
   * Replace icon at the start of the trigger button
   */
  icon?: JSX.Element
  /**
   * Hide accordion contents after closing, rather than destroying
   */
  contentAlwaysExisting?: boolean
  /**
   * Trigger test ID. This property is used for automated tests.
   */
  __testId?: string
}

export const Accordion = ({
  buttonClassName,
  children,
  className,
  margin,
  title,
  opened = false,
  noPadding,
  onOpen,
  endContent,
  icon,
  contentAlwaysExisting,
  __testId,
}: IAccordionProps) => {
  const [open, setOpen] = useState<boolean>(false)
  useEffect(() => {
    setOpen(opened)
  }, [opened])

  const getContent = () => {
    if (!contentAlwaysExisting && !open) {
      return null
    }
    return (
      <div
        data-testid={'accordion_content'}
        className={classNames(styles.body, {
          [styles['body--no-padding']]: noPadding,
          [styles['body--hidden']]: contentAlwaysExisting && !open,
        })}
      >
        {children}
      </div>
    )
  }
  return (
    <div
      data-testid={'accordion_root'}
      className={classNames(styles.root, { [styles[`margin-${margin}`]]: margin }, className || '')}
    >
      <span
        data-testid={__testId || 'accordion_expand'}
        className={classNames(
          buttonClassName ? buttonClassName : '',
          styles.trigger,
          open ? styles['trigger--opened'] : ''
        )}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          const newValue = !open
          setOpen(newValue)
          if (onOpen) {
            onOpen(newValue)
          }
        }}
      >
        {icon || (
          <div
            className={classNames(
              'kbase kbase-close-circle',
              styles.icon,
              !open && styles['icon--collapsed']
            )}
          />
        )}
        <p className={styles.title}>{title}</p>
        {endContent && endContent()}
      </span>
      {getContent()}
    </div>
  )
}
